/*
 * Comportamento de teclado exigido pelos papéis ARIA declarados no servidor.
 *
 * Regra do padrão (Docs/wes/acessibilidade.md): declarar um papel de widget ARIA obriga a
 * entregar o modelo de teclado dele. Este módulo é o outro lado desse contrato.
 *
 * Os handlers são delegados no document e registrados uma única vez, para sobreviverem aos
 * postbacks parciais (Page.endRequest reexecuta Page.initAjax).
 */
import MessagePanel from './MessagePanel';

var Accessibility = function () {
};

// Id do painel de conteúdo da master. Não é um elemento criado para isto: o landmark é aplicado
// como atributo no painel que já existe, para não alterar a estrutura do documento.
Accessibility.MAIN_CONTENT_ID = 'ContentPanel';
Accessibility.LIVE_REGION_ID = 'wes-live-region';
Accessibility.SKIP_LINK_MESSAGE_ID = 'wes-skip-link-message';

Accessibility.initialized = false;

// Widget cujo filtro disparou o postback em andamento, para anunciar o resultado ao final.
Accessibility.pendingFilterWidgetId = null;

// Espera antes de selecionar a linha alcançada pelas setas, para não gerar um postback por tecla.
Accessibility.ROW_SELECTION_DELAY = 350;
Accessibility.rowSelectionTimer = null;
Accessibility.pendingRowSelectionWidgetId = null;

// Observador da lista de opções do lookup, e sequência para dar id à lista quando ela não tem.
Accessibility.lookupResultsObserver = null;
Accessibility.lookupListSequence = 0;

// Seletores usados em mais de um ponto. Nomeados para poderem ser pesquisados: o contrato com a
// marcação gerada no servidor mora aqui, não espalhado pelo arquivo.
var TABLIST = '[role="tablist"]';
var TAB = 'a[role="tab"]';
var GRID_ROW = 'tr[rel]';
var GRID_ROW_LINK = 'a[data-row-link]';
var LOOKUP_SELECTION = '.select2-selection';
var OPEN_LOOKUP_SEARCH = '.select2-container--open .select2-search__field';
var OPEN_LOOKUP_RESULTS = '.select2-container--open .select2-results__options';
var LOOKUP_CLEAR_ICON = '.select2-selection__clear';
var HIGHLIGHTED_OPTION = '.select2-results__option--highlighted';
var LOOKUP_SEARCH_BUTTON = '.input-group.select2-bootstrap-append > .input-group-btn > a.btn';
var RECORD_COMMANDS_TOGGLE = '.dropdown-toggle.dropdown-command-record';
var EXTRA_OPTIONS_BUTTON = '.extra-options-button';
var EXTRA_OPTIONS_LIST = '.extra-options-list';
var FOOTER_COMMANDS_BAR = '.footer-commands-bar';
var SKIP_LINK = '.wes-skip-link';

// Barra de mensagens global (TopBarMainContent.ascx), reaproveitada pelo aviso do skip-link -
// mesmo elemento do aviso de "sistema desatualizado". Selecionada por classe, não por id: o site
// usa clientIDMode="AutoID", então o ClientID muda conforme o aninhamento de cada página.
var GLOBAL_MESSAGE_BAR = '.wes-global-message-bar';

// Widget do FormView e barra de comandos (Salvar/Cancelar/Novo) dele. A barra não é filha direta
// de .widget-body: o UpdatePanel do servidor (Widget.cs) sempre insere um <div class="update-panel">
// no meio, então a busca não pode depender de uma cadeia fixa de filhos diretos.
//
// widget-type carrega o nome da classe concreta (Widget.cs: this.GetType().Name), não da base -
// os formulários reais nunca são "FormView" puro, e sim uma destas subclasses. Se uma nova
// subclasse aparecer, este seletor precisa ser atualizado junto (falha silenciosa: sem erro, só
// sem efeito - foi exatamente o que aconteceu com "AjaxForm" e "WorkflowForm" na primeira versão).
var FORM_VIEW_WIDGET = '.widget[widget-type="FormView"], .widget[widget-type="AjaxForm"], .widget[widget-type="WorkflowForm"]';
var LIST_COMMANDS_BAR = '.list-commands-bar';
var COMMAND_BAR_ITEM = 'a, button, input:not([type="hidden"])';

// Qualquer elemento que hoje entra na ordem natural de TAB. Usado para achar o último campo do
// formulário e o que vem depois dele na página; o filtro de tabindex="-1" é feito à parte, com
// .not(), porque um mesmo elemento pode casar com mais de um ramo deste seletor.
var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

// Janela para o click handler do botão de mais opções ignorar o eco que o navegador sintetiza
// ao ativar por teclado um role=button - generoso o bastante para cobrir o eco, curto o
// bastante para não afetar um clique de mouse genuíno alguns instantes depois.
var ENTER_KEYDOWN_GUARD_MS = 500;

// O IE11 e o Edge legado usam nomes antigos para as teclas.
var KEY_LEFT = ['ArrowLeft', 'Left'];
var KEY_RIGHT = ['ArrowRight', 'Right'];
var KEY_UP = ['ArrowUp', 'Up'];
var KEY_DOWN = ['ArrowDown', 'Down'];
var KEY_HOME = ['Home'];
var KEY_END = ['End'];
var KEY_ENTER = ['Enter'];
var KEY_SPACE = [' ', 'Spacebar'];
var KEY_ESCAPE = ['Escape', 'Esc'];
var KEY_TAB = ['Tab'];

function isKey(event, names) {
    return names.indexOf(event.key) >= 0;
}

Accessibility.init = function () {
    // Os bindings são delegados, portanto valem para o conteúdo trocado por postback e não
    // devem ser registrados de novo a cada initAjax.
    if (Accessibility.initialized)
        return;

    Accessibility.initialized = true;

    Accessibility.initTabs();
    Accessibility.initGridRows();
    Accessibility.initLookupPopups();
    Accessibility.initMenuButtons();
    Accessibility.initSkipLink();
    Accessibility.initFormCommandsBarTabOrder();
    Accessibility.initAjax();
};

/**
 * Leva o foco ao conteúdo quando o atalho é acionado.
 *
 * Não basta o href com a âncora: o navegador rola até o destino, mas nem sempre move o foco para
 * ele, e o TAB seguinte continua a partir do próprio atalho — caindo no primeiro item do menu.
 * Aqui o foco é movido explicitamente.
 *
 * O skip-link em si (default.master) usa só sr-only, nunca sr-only-focusable: a versão anterior
 * tornava o link position:static ao ganhar foco, e ele passava a ocupar espaço e a entrar nas
 * larguras que o menu horizontal mede em JavaScript, quebrando o menu. Mas um dos critérios de
 * aceite exige que o primeiro TAB da página revele algo visível na tela - por isso, em vez de
 * revelar o próprio link, o foco nele dispara um aviso (Benner.MessagePanel, o mesmo mecanismo já
 * usado para mensagens de widget) na barra de mensagens global do topo do conteúdo, fora do
 * cabeçalho e do menu - ver GLOBAL_MESSAGE_BAR.
 */
Accessibility.initSkipLink = function () {
    $(document).on('click', SKIP_LINK, function (event) {
        event.preventDefault();
        Accessibility.focusMainContent();
    });

    $(document).on('focus', SKIP_LINK, function () {
        Accessibility.showSkipLinkMessage($(this).text());
    });

    $(document).on('blur', SKIP_LINK, function () {
        Accessibility.hideSkipLinkMessage();
    });
};

/**
 * Mostra, na barra de mensagens global (TopBarMainContent.ascx, abaixo do breadcrumb, fora do
 * cabeçalho e do menu), o convite para pular para o conteúdo.
 *
 * A barra é a mesma usada pelo aviso de "sistema desatualizado" - já existe em toda página,
 * então o convite aparece mesmo quando o primeiro widget da tela não tem painel de mensagem
 * próprio (o que descartava a alternativa anterior, ligada ao widget).
 *
 * @param {string} text texto do skip-link, já traduzido no servidor.
 */
Accessibility.showSkipLinkMessage = function (text) {
    var $bar = $(GLOBAL_MESSAGE_BAR);

    if (!$bar.length)
        return;

    // O "hidden" da barra é display:none - some elemento que for anexado dentro dela também
    // ficaria escondido.
    $bar.removeClass('hidden');

    // alert-link: cor que o Bootstrap já define para link dentro de alerta (contraste com o
    // fundo do tipo escolhido) - sem isto, o link herda a cor de link padrão do site.
    var link = '<a href="javascript:;" class="alert-link" onclick="Benner.Accessibility.focusFirstWidget(); return false;">' +
        text + '</a>';

    MessagePanel.show('info', link, Accessibility.SKIP_LINK_MESSAGE_ID, false, $bar);
};

Accessibility.hideSkipLinkMessage = function () {
    var $bar = $(GLOBAL_MESSAGE_BAR);

    MessagePanel.clear($bar);

    // Só reesconde se não sobrou nada: a barra pode estar mostrando ao mesmo tempo o aviso de
    // sistema desatualizado, renderizado pelo servidor, que não deve ser escondido por isto.
    if (!$bar.children().length)
        $bar.addClass('hidden');
};

/**
 * Foca o início do primeiro widget da tela - alvo do link mostrado por showSkipLinkMessage.
 * Prefere o título do widget, quando focável (anuncia o nome dele antes de qualquer campo);
 * sem isso, o primeiro campo alcançável por TAB; sem widget nenhum, o landmark de conteúdo.
 */
Accessibility.focusFirstWidget = function () {
    var $widget = $('#' + Accessibility.MAIN_CONTENT_ID).find('.widget').first();

    if (!$widget.length) {
        Accessibility.focusMainContent();
        return;
    }

    var $caption = $widget.find('.portlet-title > .caption[tabindex]').first();

    if ($caption.length) {
        $caption.focus();
        return;
    }

    var $field = Accessibility.focusableIn($widget).first();

    if ($field.length)
        $field.focus();
    else
        Accessibility.focusMainContent();
};

/**
 * Ajustes que precisam ser reaplicados depois de cada postback parcial, porque o conteúdo é
 * recriado. Chamado de Page.initAjax.
 *
 * Todo o corpo é protegido: este código é acessório, e uma falha aqui não pode interromper a
 * construção dos componentes da tela.
 */
Accessibility.initAjax = function () {
    // Cada ajuste é isolado. Um try em volta de todos fazia a falha do primeiro cancelar os
    // seguintes sem deixar rastro na tela — foi assim que o botão de mais opções ficou sem a
    // parada de TAB, sem que nada indicasse o motivo.
    runStep('prepareFormCommandsBar', Accessibility.prepareFormCommandsBar);
    runStep('nameLookupFields', Accessibility.nameLookupFields);
    runStep('nameLookupSearchButtons', Accessibility.nameLookupSearchButtons);
    runStep('nameRecordCommandMenus', Accessibility.nameRecordCommandMenus);
    runStep('prepareExtraOptionsButtons', Accessibility.prepareExtraOptionsButtons);
    runStep('hideDuplicatedCommandBar', Accessibility.hideDuplicatedCommandBar);
};

/**
 * Executa um ajuste de acessibilidade sem deixar a falha dele atingir os demais nem a construção
 * da tela. Este código é acessório: ele melhora a página, mas não pode impedi-la de funcionar.
 *
 * @param {string} name nome do ajuste, para identificar a falha no console.
 * @param {Function} step ajuste a executar.
 */
function runStep(name, step) {
    try {
        step();
    } catch (e) {
        console.error('Accessibility.' + name + ': ' + e.message);
    }
}

/* ------------------------------------------------------------------------------------------
 * Abas
 * ----------------------------------------------------------------------------------------*/

/**
 * Navegação por setas entre abas, conforme o padrão WAI-ARIA. O servidor renderiza o foco
 * itinerante (apenas a aba ativa tem tabindex zero), então o TAB entra e sai do conjunto de
 * abas numa única parada e as setas percorrem as abas.
 */
Accessibility.initTabs = function () {
    $(document).on('keydown', TABLIST + ' > li > ' + TAB, function (event) {
        var $tab = $(this);
        var $tabs = Accessibility.visibleTabsOf($tab);
        var index = $tabs.index($tab);

        if (index < 0)
            return;

        // Espaço ativa a aba que já está focada.
        if (isKey(event, KEY_SPACE)) {
            event.preventDefault();
            $tab.trigger('click');
            return;
        }

        var target = null;

        if (isKey(event, KEY_RIGHT) || isKey(event, KEY_DOWN))
            target = (index + 1) % $tabs.length;
        else if (isKey(event, KEY_LEFT) || isKey(event, KEY_UP))
            target = (index - 1 + $tabs.length) % $tabs.length;
        else if (isKey(event, KEY_HOME))
            target = 0;
        else if (isKey(event, KEY_END))
            target = $tabs.length - 1;

        if (target === null)
            return;

        // As setas apenas movem o foco, sem ativar, para que o usuário possa percorrer as abas
        // antes de escolher. Enter e Espaço ativam — o Enter é tratado pelo próprio navegador,
        // que dispara o clique da âncora.
        event.preventDefault();
        Accessibility.focusTab($tabs, target);
    });

    // Ao ativar uma aba, o tabindex itinerante precisa acompanhar a nova aba selecionada,
    // senão o TAB voltaria para a aba anterior.
    $(document).on('shown.bs.tab', TAB, function () {
        var $tab = $(this);
        var $tabs = Accessibility.visibleTabsOf($tab);

        $tabs.attr('tabindex', '-1').attr('aria-selected', 'false');
        $tab.attr('tabindex', '0').attr('aria-selected', 'true');
    });
};

/**
 * Abas do mesmo tablist que estão visíveis. O plugin bootstrap-tabdrop move as abas que não
 * couberam para dentro de um dropdown; essas não devem entrar na navegação por setas.
 */
Accessibility.visibleTabsOf = function ($tab) {
    return $tab.closest(TABLIST)
        .find('> li:visible > ' + TAB);
};

Accessibility.focusTab = function ($tabs, index) {
    var $target = $tabs.eq(index);

    if (!$target.length)
        return;

    $tabs.attr('tabindex', '-1');
    $target.attr('tabindex', '0');
    $target.focus();
};

/* ------------------------------------------------------------------------------------------
 * Barra de comandos do formulário (Salvar/Cancelar/Novo)
 * ----------------------------------------------------------------------------------------*/

/*
 * A barra de comandos do FormView é renderizada antes dos campos, porque é ali que ela aparece
 * visualmente - no topo do formulário. Isso a tornava a primeira parada de TAB, antes de
 * qualquer campo (WCAG 2.4.3 - ordem de foco significativa). Mover a marcação no servidor
 * também move a posição visual, e desacoplar as duas coisas com CSS (flex + order) quebrou o
 * layout em outras telas que dependem do fluxo normal do documento dentro do widget.
 *
 * A solução aqui não toca em posição nem em CSS: os botões da barra saem da ordem natural de
 * TAB (tabindex="-1") e o teclado é gerenciado manualmente, do mesmo jeito que já é feito com
 * as abas e as linhas do grid, para que o TAB alcance os campos primeiro e só depois a barra.
 *
 * Vale só para o FormView - Grid, PivotTable, AmCharts e FilterWidget usam a mesma barra para
 * ações como Novo/Excluir/Exportar, e ali ela deve continuar sendo a primeira parada.
 *
 * A ordem vale nos dois sentidos: TAB no último campo entra na barra pela frente, SHIFT+TAB em
 * quem estiver logo depois do formulário na página entra nela por trás - ver
 * handleShiftTabAfterFormWidget.
 */

Accessibility.initFormCommandsBarTabOrder = function () {
    $(document).on('keydown', function (event) {
        if (!isKey(event, KEY_TAB))
            return;

        Accessibility.handleFormCommandsBarTab(event);
    });
};

/**
 * Barra de comandos do próprio widget, sem contar as de widgets aninhados dentro dele (um grid
 * dentro de uma aba do formulário, por exemplo). A busca é por .closest(), não por uma cadeia
 * fixa de filhos diretos, porque o UpdatePanel do servidor sempre insere um nível a mais entre
 * .widget-body e a barra - ver comentário de LIST_COMMANDS_BAR.
 *
 * @param {jQuery} $widget widget do FormView.
 */
Accessibility.formCommandsBarOf = function ($widget) {
    return $widget.find(LIST_COMMANDS_BAR).filter(function () {
        return $(this).closest('.widget').is($widget);
    });
};

/**
 * Widget do FormView a que a barra pertence, ou vazio se a barra for de um widget aninhado (caso
 * em que closest('.widget[widget-type="FormView"]') alcançaria erradamente um FormView externo).
 *
 * @param {jQuery} $bar barra de comandos.
 */
Accessibility.formViewWidgetOfCommandsBar = function ($bar) {
    var $widget = $bar.closest(FORM_VIEW_WIDGET);

    if (!$widget.length || !$bar.closest('.widget').is($widget))
        return $();

    return $widget;
};

/**
 * Tira os itens da barra de comandos do formulário da ordem natural de TAB. Precisa ser
 * reaplicado a cada postback parcial, porque o FormView recria o próprio conteúdo.
 */
Accessibility.prepareFormCommandsBar = function () {
    $(FORM_VIEW_WIDGET).each(function () {
        Accessibility.formCommandsBarOf($(this))
            .find(COMMAND_BAR_ITEM)
            .attr('tabindex', '-1');
    });
};

/**
 * Botões visíveis de uma barra de comandos, na ordem em que aparecem.
 *
 * @param {jQuery} $bar barra de comandos.
 */
function commandItemsOf($bar) {
    return $bar.find(COMMAND_BAR_ITEM).filter(':visible');
}

/**
 * Trata o TAB quando o foco está no último campo do formulário (leva à barra de comandos) ou
 * dentro da própria barra (percorre os botões e sai nas duas pontas).
 */
Accessibility.handleFormCommandsBarTab = function (event) {
    var active = document.activeElement;
    var $bar = $(active).closest(LIST_COMMANDS_BAR);
    var $barWidget = $bar.length ? Accessibility.formViewWidgetOfCommandsBar($bar) : $();

    if ($barWidget.length) {
        Accessibility.moveWithinFormCommandsBar(event, $bar, $barWidget, active);
        return;
    }

    // Fora da barra, só interessam as duas pontas: sair do último campo (TAB) ou entrar vindo de
    // depois do formulário (SHIFT+TAB) - nos demais casos o TAB segue o comportamento nativo.
    if (event.shiftKey) {
        Accessibility.handleShiftTabAfterFormWidget(event, active);
        return;
    }

    var $widget = $(active).closest(FORM_VIEW_WIDGET);

    if (!$widget.length)
        return;

    var $fields = Accessibility.focusableIn($widget);

    if (!$fields.length || $fields.last().get(0) !== active)
        return;

    event.preventDefault();
    Accessibility.focusFirstFormCommand($widget);
};

/**
 * Trata o SHIFT+TAB de quem está no primeiro elemento alcançável por TAB depois de um formulário
 * - o espelho de exitFormCommandsBar. Sem isto, entrar num formulário de trás para frente pula a
 * barra de comandos direto para o último campo, porque os botões da barra estão fora da ordem
 * natural (tabindex="-1") e o SHIFT+TAB nativo simplesmente os ignora.
 *
 * Roda em qualquer SHIFT+TAB da página, não só perto de formulários: o custo (uma busca no
 * documento inteiro) só é pago nesse evento raro, e o resultado é vazio para o caso comum de não
 * haver nenhum FormView logo antes do elemento focado.
 */
Accessibility.handleShiftTabAfterFormWidget = function (event, active) {
    var $all = Accessibility.focusableIn($(document));
    var index = $all.index(active);

    if (index <= 0)
        return;

    var $widget = $($all.get(index - 1)).closest(FORM_VIEW_WIDGET);

    // Sem essa checagem, o SHIFT+TAB entre dois campos comuns do mesmo formulário também cairia
    // aqui, porque o campo anterior também está "dentro de um FormView".
    if (!$widget.length || $.contains($widget.get(0), active))
        return;

    event.preventDefault();
    Accessibility.focusLastFormCommand($widget);
};

/**
 * Move o foco entre os botões da barra, ou sai dela nas duas pontas: para a frente, para o que
 * vem depois do formulário na página; para trás, para o último campo do formulário.
 */
Accessibility.moveWithinFormCommandsBar = function (event, $bar, $widget, active) {
    var $items = commandItemsOf($bar);
    var index = $items.index(active);

    if (index < 0)
        return;

    event.preventDefault();

    if (event.shiftKey) {
        if (index === 0)
            Accessibility.focusLastFormField($widget);
        else
            $items.eq(index - 1).focus();
        return;
    }

    if (index === $items.length - 1)
        Accessibility.exitFormCommandsBar($widget);
    else
        $items.eq(index + 1).focus();
};

/**
 * Foca o primeiro botão da barra de comandos do formulário.
 *
 * @param {jQuery} $widget widget do FormView.
 */
Accessibility.focusFirstFormCommand = function ($widget) {
    var $items = commandItemsOf(Accessibility.formCommandsBarOf($widget));

    if ($items.length)
        $items.first().focus();
};

/**
 * Foca o último botão da barra de comandos do formulário.
 *
 * @param {jQuery} $widget widget do FormView.
 */
Accessibility.focusLastFormCommand = function ($widget) {
    var $items = commandItemsOf(Accessibility.formCommandsBarOf($widget));

    if ($items.length)
        $items.last().focus();
};

/**
 * Foca o último campo alcançável por TAB do formulário.
 *
 * @param {jQuery} $widget widget do FormView.
 */
Accessibility.focusLastFormField = function ($widget) {
    var $fields = Accessibility.focusableIn($widget);

    if ($fields.length)
        $fields.last().focus();
};

/**
 * Foca o próximo elemento alcançável por TAB depois do formulário - como se a barra de comandos
 * estivesse de fato na última posição do documento.
 *
 * @param {jQuery} $widget widget do FormView.
 */
Accessibility.exitFormCommandsBar = function ($widget) {
    var widget = $widget.get(0);

    if (!widget)
        return;

    var $all = Accessibility.focusableIn($(document));
    var lastInsideIndex = -1;

    $all.each(function (index) {
        if ($.contains(widget, this))
            lastInsideIndex = index;
    });

    var $next = $all.eq(lastInsideIndex + 1);

    if ($next.length)
        $next.focus();
    else
        Accessibility.focusMainContent();
};

/**
 * Elementos hoje alcançáveis por TAB dentro do escopo, na ordem em que aparecem no documento.
 *
 * @param {jQuery} $scope escopo da busca.
 */
Accessibility.focusableIn = function ($scope) {
    return $scope.find(FOCUSABLE).not('[tabindex="-1"]').filter(':visible');
};

/* ------------------------------------------------------------------------------------------
 * Linhas do grid
 * ----------------------------------------------------------------------------------------*/

/**
 * Ativação e navegação por teclado nas linhas do grid.
 *
 * O servidor marca com data-row-link a única âncora da linha que entra na ordem de TAB (ver
 * SimpleGrid.MakeRowKeyboardAccessible). Aqui vem o resto do contrato: Enter e Espaço ativam, e
 * as setas deslocam entre linhas — sem isso um grid de trinta linhas exigiria trinta TABs para
 * ser atravessado.
 */
Accessibility.initGridRows = function () {
    $(document).on('keydown', GRID_ROW + ' ' + GRID_ROW_LINK, function (event) {
        var $link = $(this);

        if (isKey(event, KEY_ENTER) || isKey(event, KEY_SPACE)) {
            Accessibility.activateRowLink($link, event);
            return;
        }

        Accessibility.moveToAdjacentRow($link, event);
    });
};

/**
 * Aciona a linha focada.
 *
 * @param {jQuery} $link âncora da linha.
 * @param {Object} event evento de teclado.
 */
Accessibility.activateRowLink = function ($link, event) {
    // Com href o navegador já ativa o link no Enter; disparar de novo executaria a ação duas
    // vezes. O Espaço nunca ativa link nativamente, então é sempre tratado aqui.
    if (!isKey(event, KEY_SPACE) && $link.attr('href'))
        return;

    event.preventDefault();
    $link.trigger('click');
};

/**
 * Move o foco para a linha vizinha, quando a tecla for seta, e agenda a seleção dela.
 *
 * @param {jQuery} $link âncora da linha.
 * @param {Object} event evento de teclado.
 */
Accessibility.moveToAdjacentRow = function ($link, event) {
    var delta = 0;

    if (isKey(event, KEY_DOWN))
        delta = 1;
    else if (isKey(event, KEY_UP))
        delta = -1;
    else
        return;

    event.preventDefault();

    var $target = Accessibility.focusRowLink($link, delta);

    if ($target.length)
        Accessibility.scheduleRowSelection($target);
};

/**
 * Agenda a seleção da linha que acabou de receber o foco.
 *
 * O disparo é adiado de propósito: a seleção é um postback, e percorrer dez linhas com a seta
 * faria dez postbacks encadeados. Com o adiamento, quem atravessa o grid rapidamente gera um
 * único postback, no momento em que para.
 *
 * @param {jQuery} $link âncora da linha que recebeu o foco.
 */
Accessibility.scheduleRowSelection = function ($link) {
    if (Accessibility.rowSelectionTimer)
        clearTimeout(Accessibility.rowSelectionTimer);

    Accessibility.rowSelectionTimer = setTimeout(function () {
        Accessibility.rowSelectionTimer = null;
        Accessibility.selectRow($link);
    }, Accessibility.ROW_SELECTION_DELAY);
};

/**
 * Seleciona a linha, exatamente como o clique do mouse faz.
 *
 * @param {jQuery} $link âncora da linha.
 */
Accessibility.selectRow = function ($link) {
    var $row = $link.closest(GRID_ROW);

    // A seleção é o onclick que o servidor coloca nas células. Ele não existe quando a visão
    // desabilita a seleção de linha; nesse caso a seta apenas move o foco, sem postback.
    var cell = $row.find('td[onclick]').get(0);

    if (!cell)
        return;

    // O grid é recriado pelo postback, então a linha precisa ser reencontrada depois.
    Accessibility.pendingRowSelectionWidgetId = $row.closest('.widget').attr('id') || null;

    cell.click();
};

/**
 * Devolve o foco à linha selecionada depois do postback da seleção. Sem isto o foco iria para o
 * primeiro campo do widget e o usuário perderia o lugar no grid.
 */
Accessibility.restoreSelectedRowFocus = function () {
    var widgetId = Accessibility.pendingRowSelectionWidgetId;

    if (!widgetId)
        return;

    Accessibility.pendingRowSelectionWidgetId = null;

    var $link = $('#' + widgetId).find('tr.active ' + GRID_ROW_LINK).first();

    if ($link.length)
        $link.focus();
};

/**
 * Linhas de dados do grid a que a âncora pertence.
 *
 * Um grid pode conter outro, e find alcança a tabela aninhada; por isso o filtro pela tabela de
 * origem, que garante que a navegação não salte para o grid de dentro.
 *
 * @param {jQuery} $link âncora de uma das linhas.
 * @returns {jQuery} as linhas do mesmo grid, ou vazio se a âncora não está numa tabela.
 */
Accessibility.rowsOfSameGrid = function ($link) {
    var $table = $link.closest('table');
    var table = $table.get(0);

    if (!table)
        return $();

    return $table.find(GRID_ROW).filter(function () {
        return $(this).closest('table').get(0) === table;
    });
};

/**
 * Move o foco para a linha anterior ou seguinte do mesmo grid.
 *
 * @param {jQuery} $link âncora que está com o foco.
 * @param {number} delta -1 para a linha anterior, 1 para a seguinte.
 * @returns {jQuery} a âncora que recebeu o foco, ou vazio se não houve para onde ir.
 */
Accessibility.focusRowLink = function ($link, delta) {
    var $rows = Accessibility.rowsOfSameGrid($link);
    var index = $rows.index($link.closest(GRID_ROW)) + delta;

    if (index < 0 || index >= $rows.length)
        return $();

    var $target = $rows.eq(index).find(GRID_ROW_LINK).first();

    $target.focus();

    return $target;
};

/* ------------------------------------------------------------------------------------------
 * Campos lookup (Select2)
 * ----------------------------------------------------------------------------------------*/

/*
 * O Select2 desta versão monta o campo com semântica incompleta, e nada disto é lido de volta
 * por ele — as opções são localizadas por classe e por aria-selected, nunca por role. Por isso
 * os ajustes abaixo corrigem o que o leitor de tela anuncia sem alterar o funcionamento:
 *
 *   select2.js:1549  aria-labelledby do combo aponta para o span do VALOR, então o nome do
 *                    campo passa a ser o valor e o rótulo nunca é lido;
 *   select2.js:1372  aria-haspopup="true" equivale a "menu", mas o popup não é um menu;
 *   select2.js:794   a lista de resultados é role="tree";
 *   select2.js:962   cada opção é role="treeitem".
 *
 * O rótulo não pode ser associado pelo servidor: o <label for> aponta para o span que envolve o
 * campo, que não é elemento rotulável, e o <select> original fica com aria-hidden.
 */

Accessibility.initLookupPopups = function () {
    // O evento é disparado no <select> original e borbulha, então a delegação sobrevive aos
    // postbacks sem precisar rebind.
    $(document).on('select2:open', function (event) {
        try {
            Accessibility.fixLookupResults($(event.target));
        } catch (e) {
            console.error('Accessibility.initLookupPopups: ' + e.message);
        }
    });

    $(document).on('select2:close', function () {
        Accessibility.disconnectLookupObserver();

        // O campo de busca é reaproveitado entre aberturas; deixar aria-expanded verdadeiro
        // depois de fechar informaria um estado que não existe mais.
        $('.select2-search__field[role="combobox"]').attr('aria-expanded', 'false');
    });
};

/**
 * Dá nome acessível aos campos lookup já renderizados.
 */
Accessibility.nameLookupFields = function () {
    Accessibility.hideLookupClearIcon();

    $(LOOKUP_SELECTION).each(function () {
        var $selection = $(this);

        if ($selection.attr('data-wes-named'))
            return;

        $selection.attr('data-wes-named', 'true');

        // O popup é uma lista de opções, não um menu.
        $selection.attr('aria-haspopup', 'listbox');

        Accessibility.watchLookupClearIcon($selection);
        Accessibility.nameLookupField($selection);
    });
};

/**
 * Define o nome acessível de um campo lookup.
 *
 * @param {jQuery} $selection elemento focável do Select2.
 */
Accessibility.nameLookupField = function ($selection) {
    var labelId = Accessibility.findLookupLabelId($selection);

    if (labelId) {
        $selection.attr('aria-labelledby', labelId);
        return;
    }

    // Sem rótulo associado — caso do lookup dentro de grid — resta o título que o servidor grava
    // no campo oculto do valor.
    var label = $selection.closest('.input-group').find('input[data-label]').attr('data-label');

    if (!label)
        return;

    $selection.removeAttr('aria-labelledby');
    $selection.attr('aria-label', label);
};

/**
 * Esconde do leitor de tela o "×" que limpa o campo.
 *
 * Ele fica dentro do span que contém o valor, então entra no que é anunciado como conteúdo do
 * campo — e "×" é lido como "vezes", produzindo anúncios do tipo "vezes Abaira". Não é perda de
 * função: o "×" não tem tabindex, portanto nunca foi alcançável pelo teclado. Continua clicável
 * pelo mouse.
 */
Accessibility.hideLookupClearIcon = function (context) {
    $(context || document).find(LOOKUP_CLEAR_ICON + ':not([aria-hidden])')
        .attr('aria-hidden', 'true');
};

/**
 * Mantém o "×" escondido do leitor de tela ao longo da vida do campo.
 *
 * Não basta aplicar uma vez: AllowClear.prototype.update (select2.js:1851-1866) insere um span
 * novo a cada re-renderização da seleção, e isso é disparado pelo evento interno selection:update
 * — que ocorre ao escolher um valor, ao alterá-lo por script e depois de postback. Escutar apenas
 * o evento público select2:select deixava vários desses caminhos de fora.
 *
 * Por isso a observação é do próprio container do valor, que o Select2 cria uma única vez e nunca
 * substitui: só o conteúdo dele muda.
 *
 * @param {jQuery} $selection elemento focável do Select2.
 */
Accessibility.watchLookupClearIcon = function ($selection) {
    if (typeof MutationObserver === 'undefined')
        return;

    var rendered = $selection.find('.select2-selection__rendered').get(0);

    if (!rendered)
        return;

    var observer = new MutationObserver(function () {
        Accessibility.hideLookupClearIcon(rendered);
    });

    // Apenas inclusão de nós: a troca de aria-hidden feita aqui não realimenta o observador.
    observer.observe(rendered, { childList: true });
};

/**
 * Id do rótulo do campo. O label aponta, via for, para o span que envolve o campo inteiro.
 *
 * @param {jQuery} $selection elemento focável do Select2.
 * @returns {string} id do rótulo, ou vazio.
 */
Accessibility.findLookupLabelId = function ($selection) {
    var wrapperId = $selection.parents('[id]').first().attr('id');

    if (!wrapperId)
        return '';

    return $('label[for="' + wrapperId + '"]').attr('id') || '';
};

/**
 * Corrige os papéis da lista de opções aberta, para o leitor de tela anunciá-las.
 *
 * @param {jQuery} $select o select original que abriu o popup.
 */
Accessibility.fixLookupResults = function ($select) {
    var $results = $(OPEN_LOOKUP_RESULTS);

    if (!$results.length)
        return;

    Accessibility.linkLookupSearchToResults($select, $results);

    var apply = function () {
        $results.attr('role', 'listbox');
        $results.find('[role="treeitem"]').attr('role', 'option');

        Accessibility.syncLookupActiveOption($results);
    };

    apply();

    // Duas razões para observar: no lookup que busca no servidor as opções chegam depois da
    // abertura, e a opção destacada muda de classe a cada seta. O filtro de atributos é apenas
    // class, então as trocas de role feitas aqui não realimentam o observador.
    if (typeof MutationObserver === 'undefined')
        return;

    Accessibility.disconnectLookupObserver();

    Accessibility.lookupResultsObserver = new MutationObserver(apply);
    Accessibility.lookupResultsObserver.observe($results.get(0), {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
    });
};

/**
 * Copia a opção destacada para o aria-activedescendant do campo de busca.
 *
 * O Select2 só grava o descendente ativo em dois lugares: no elemento da seleção
 * (select2.js:1416) e no campo de busca embutido do multi-select (select2.js:1930). Para o
 * select simples, cujo campo de busca fica dentro do dropdown, não existe tratamento — e é
 * justamente esse campo que tem o foco enquanto a lista está aberta. Sem o descendente ativo no
 * elemento focado, o leitor de tela não tem o que anunciar ao percorrer as opções.
 *
 * @param {jQuery} $results a lista de opções aberta.
 */
Accessibility.syncLookupActiveOption = function ($results) {
    var $search = $(OPEN_LOOKUP_SEARCH);

    if (!$search.length)
        return;

    var highlightedId = $results.find(HIGHLIGHTED_OPTION).attr('id');

    if (highlightedId)
        $search.attr('aria-activedescendant', highlightedId);
    else
        $search.removeAttr('aria-activedescendant');
};

/**
 * Liga o campo de busca do dropdown à lista de opções.
 *
 * Com a lista aberta o foco vai para o campo de busca, e é nele que o Select2 grava o
 * aria-activedescendant com a opção destacada (select2.js:1931). Mas esse campo é
 * role="textbox" e não declara relação nenhuma com a lista, que fica em outro ponto do
 * documento. Sem a relação, o leitor de tela não resolve a opção referenciada e anuncia em
 * branco, mesmo com a opção correta no DOM.
 *
 * O padrão de combobox com lista exige, no campo: role combobox, aria-expanded e aria-controls
 * apontando para a lista. Só com isso o aria-activedescendant passa a ser resolvido.
 *
 * @param {jQuery} $select o select original que abriu o popup.
 * @param {jQuery} $results a lista de opções aberta.
 */
Accessibility.linkLookupSearchToResults = function ($select, $results) {
    var $search = $(OPEN_LOOKUP_SEARCH);

    if (!$search.length)
        return;

    var listId = $results.attr('id');

    if (!listId) {
        Accessibility.lookupListSequence++;
        listId = 'wes-select2-results-' + Accessibility.lookupListSequence;
        $results.attr('id', listId);
    }

    $search.attr('role', 'combobox');
    $search.attr('aria-expanded', 'true');
    $search.attr('aria-controls', listId);
    $search.attr('aria-autocomplete', 'list');

    // O campo de busca também precisa de nome próprio: sem ele o leitor de tela anuncia apenas
    // "edição" ao entrar na lista. Reaproveita o rótulo do campo que abriu o dropdown.
    if ($search.attr('aria-labelledby') || $search.attr('aria-label'))
        return;

    var $selection = $select.siblings('.select2-container').find(LOOKUP_SELECTION);
    var labelId = $selection.attr('aria-labelledby');

    if (labelId)
        $search.attr('aria-labelledby', labelId);
    else if ($selection.attr('aria-label'))
        $search.attr('aria-label', $selection.attr('aria-label'));
};

Accessibility.disconnectLookupObserver = function () {
    if (!Accessibility.lookupResultsObserver)
        return;

    Accessibility.lookupResultsObserver.disconnect();
    Accessibility.lookupResultsObserver = null;
};

/* ------------------------------------------------------------------------------------------
 * Botões que abrem menu
 * ----------------------------------------------------------------------------------------*/

/*
 * Estes ajustes são feitos aqui, e não na marcação do servidor, de propósito: trocar a tag ou
 * acrescentar href nesses elementos alterou layout e comportamento em tentativas anteriores.
 * Atributo e handler de teclado não mexem em nenhum dos dois.
 */

Accessibility.initMenuButtons = function () {
    // A tecla no próprio botão não é tratada aqui: ela chega por um atributo onkeydown que o
    // servidor emite (ExtraOptionsColumnHeader), e cai em onExtraOptionsKeyDown.

    // Leitor de tela não entrega a tecla à página: ele ativa um role=button disparando um click
    // sintetizado. Sem este handler, ENTER no NVDA não abre o menu por mais correto que esteja o
    // tratamento de keydown. O clique real do mouse tem detail maior que zero e é ignorado aqui,
    // porque para ele o hover de Grid.js já abriu a lista — tratá-lo faria o clique fechar o que
    // o hover acabou de abrir.
    //
    // Teclado sem leitor de tela também dispara este click: o navegador sintetiza um click com
    // detail 0 ao ativar por Enter/Espaço um elemento role=button, além de entregar o keydown à
    // página. Sem a marca ENTER_KEYDOWN_GUARD_MS, esse click eco chegava aqui pouco depois do
    // onExtraOptionsKeyDown já ter aberto a lista, e o fechava de volta - abria e fechava na
    // mesma tecla, sem nenhum erro para indicar o motivo.
    $(document).on('click', EXTRA_OPTIONS_BUTTON, function (event) {
        var $button = $(this);
        var keydownAt = $button.data('extra-options-keydown-at');

        if (keydownAt && (Date.now() - keydownAt) < ENTER_KEYDOWN_GUARD_MS)
            return;

        if (event.detail !== 0)
            return;

        event.preventDefault();

        Accessibility.toggleExtraOptions($button, !Accessibility.isExtraOptionsOpen($button));
    });

    // Fecha ao sair pelo TAB, mas não quando o foco entra na própria lista, que é para onde a
    // abertura pelo teclado manda o foco.
    $(document).on('focusout', EXTRA_OPTIONS_BUTTON, function (event) {
        var list = Accessibility.extraOptionsListOf($(this)).get(0);

        if (list && event.relatedTarget && $.contains(list, event.relatedTarget))
            return;

        Accessibility.toggleExtraOptions($(this), false);
    });

    // Dentro da lista, ESC devolve o foco ao botão — sem isso o foco ficaria num menu fechado.
    $(document).on('keydown', EXTRA_OPTIONS_LIST, function (event) {
        if (!isKey(event, KEY_ESCAPE))
            return;

        var $button = Accessibility.extraOptionsButtonOf($(this));

        Accessibility.toggleExtraOptions($button, false);
        $button.focus();
    });

    // E o TAB que sai da lista fecha o menu, como fecharia se saísse do botão.
    $(document).on('focusout', EXTRA_OPTIONS_LIST, function (event) {
        if (event.relatedTarget && $.contains(this, event.relatedTarget))
            return;

        Accessibility.toggleExtraOptions(Accessibility.extraOptionsButtonOf($(this)), false);
    });

    // O mouse abre e fecha pelos handlers que já existem em Grid.js; aqui só o estado acompanha.
    $(document).on('mouseenter', EXTRA_OPTIONS_BUTTON, function () {
        $(this).attr('aria-expanded', 'true');
    });

    $(document).on('mouseleave', EXTRA_OPTIONS_BUTTON, function () {
        $(this).attr('aria-expanded', 'false');
    });

    // O dropdown de comandos do registro é do Bootstrap, que já trata o teclado; só o estado
    // precisa ser espelhado no botão.
    $(document).on('shown.bs.dropdown hidden.bs.dropdown', function (event) {
        $(event.target).find(RECORD_COMMANDS_TOGGLE)
            .attr('aria-expanded', event.type === 'shown' ? 'true' : 'false');
    });
};

/**
 * Trata a tecla pressionada sobre o botão de mais opções.
 *
 * É chamada pelo atributo onkeydown que o servidor emite no botão, e não por um handler
 * delegado, para que o teclado funcione mesmo que a inicialização do módulo não tenha ocorrido.
 *
 * @param {Element} element botão de mais opções.
 * @param {KeyboardEvent} event evento nativo do teclado.
 */
Accessibility.onExtraOptionsKeyDown = function (element, event) {
    var $button = $(element);

    if (isKey(event, KEY_ENTER) || isKey(event, KEY_SPACE)) {
        // Sem isto o ENTER seguiria para o keypress, onde o WebForms dispara o botão padrão do
        // formulário e provoca um postback no lugar de abrir o menu.
        event.preventDefault();
        // Marca o instante: o click handler em initMenuButtons usa isto para ignorar o click
        // eco que o próprio navegador sintetiza ao ativar por teclado um role=button.
        $button.data('extra-options-keydown-at', Date.now());
        Accessibility.toggleExtraOptions($button, !Accessibility.isExtraOptionsOpen($button));
        return;
    }

    if (isKey(event, KEY_ESCAPE))
        Accessibility.toggleExtraOptions($button, false);
};

/**
 * Abre ou fecha a lista de mais opções, reaproveitando os handlers de mouse que Grid.js já
 * registra — assim o posicionamento da lista continua sendo calculado num lugar só.
 *
 * @param {jQuery} $button botão de mais opções.
 * @param {boolean} open verdadeiro para abrir.
 */
Accessibility.toggleExtraOptions = function ($button, open) {
    var $list = Accessibility.extraOptionsListOf($button);

    if (!$list.length) {
        console.error('Accessibility.toggleExtraOptions: lista de mais opções não encontrada.');
        return;
    }

    if (!open) {
        triggerMouseEvent($button, 'mouseleave');
        $list.hide();
        $button.attr('aria-expanded', 'false');
        return;
    }

    // Primeiro o evento de mouse: quando Grid.js vinculou este grid, é ele quem calcula a
    // posição da lista, e o teclado passa a abrir exatamente como o mouse abre.
    triggerMouseEvent($button, 'mouseenter');

    // Esse vínculo nem sempre existe: ele é feito por widget, a partir de Grid.widgets, e é
    // refeito a cada postback. Quando falta, o trigger não exibe nada — e exibir a lista sem
    // calcular a posição também não resolve, porque ela é absoluta e apareceria no canto do
    // painel, longe do botão. Por isso aqui existe o caminho completo, e não só o show.
    if (!$list.is(':visible')) {
        positionExtraOptionsList($button, $list);
        $list.show();
    }

    $button.attr('aria-expanded', 'true');

    // Padrão WAI-ARIA de botão de menu: abrir pelo teclado leva o foco para o primeiro item,
    // senão o TAB seguinte sairia do menu recém-aberto e ele fecharia sem ter sido usado.
    $list.find('a').first().focus();
};

/**
 * Dispara em nome do teclado o evento de mouse que Grid.js escuta.
 *
 * A chamada é isolada porque o handler do outro módulo roda dentro desta função: uma falha lá
 * interromperia a abertura pelo teclado antes do caminho próprio, que existe logo em seguida.
 *
 * @param {jQuery} $button botão de mais opções.
 * @param {string} type nome do evento.
 */
function triggerMouseEvent($button, type) {
    try {
        $button.trigger(type);
    } catch (e) {
        console.error('Accessibility.toggleExtraOptions: ' + e.message);
    }
}

/**
 * Posiciona a lista logo abaixo do botão, no sistema de coordenadas do elemento que a posiciona.
 *
 * Só é usada quando Grid.js não vinculou o grid: a conta é a mesma de setExtraOptionsPosition,
 * que é privada daquele módulo e não pode ser reaproveitada daqui.
 *
 * @param {jQuery} $button botão de mais opções.
 * @param {jQuery} $list lista de mais opções.
 */
function positionExtraOptionsList($button, $list) {
    var container = $list.offsetParent();

    if (!container.length || !$button.length)
        return;

    var buttonOffset = $button.offset();
    var containerOffset = container.offset();

    $list.css({
        top: (buttonOffset.top - containerOffset.top + $button.outerHeight()) + 'px',
        left: (buttonOffset.left - containerOffset.left) + 'px'
    });
}

/**
 * Estado real da lista, lido do DOM.
 *
 * O estado não pode ser deduzido do aria-expanded do botão: ele também reflete o hover do mouse,
 * e confiar nele fazia o Enter tentar fechar uma lista que nunca havia sido aberta.
 *
 * @param {jQuery} $button botão de mais opções.
 */
Accessibility.isExtraOptionsOpen = function ($button) {
    return Accessibility.extraOptionsListOf($button).is(':visible');
};

/**
 * Lista de mais opções do mesmo grid.
 *
 * O servidor renderiza a lista fora do cabeçalho, dentro do painel do grid, então ela não é
 * irmã nem filha do botão: a busca precisa subir até um ancestral comum. Nem todo grid está
 * dentro de um .widget — os que são montados dentro de outra tela ficam só dentro do painel de
 * atualização — por isso o ancestral é procurado do mais específico para o mais genérico.
 *
 * @param {jQuery} $button botão de mais opções.
 */
Accessibility.extraOptionsListOf = function ($button) {
    var key = $button.attr('data-extra-options-button');

    if (key) {
        // O primeiro é sempre a lista externa: os submenus reaproveitam a mesma marca e são
        // renderizados dentro dela.
        var $byKey = $(EXTRA_OPTIONS_LIST + '[data-extra-options="' + key + '"]').first();

        if ($byKey.length)
            return $byKey;
    }

    return closestSibling($button, EXTRA_OPTIONS_LIST);
};

/**
 * Botão que abre a lista de mais opções, a partir da lista. É o caminho inverso de
 * extraOptionsListOf, usado pelos handlers vinculados à lista.
 *
 * @param {jQuery} $list lista de mais opções.
 */
Accessibility.extraOptionsButtonOf = function ($list) {
    var key = $list.closest(EXTRA_OPTIONS_LIST + '[data-extra-options]').attr('data-extra-options');

    if (key) {
        var $byKey = $(EXTRA_OPTIONS_BUTTON + '[data-extra-options-button="' + key + '"]').first();

        if ($byKey.length)
            return $byKey;
    }

    return closestSibling($list, EXTRA_OPTIONS_BUTTON);
};

/**
 * Procura um elemento que compartilhe com a origem o ancestral mais próximo possível.
 *
 * @param {jQuery} $origin elemento de partida.
 * @param {string} selector seletor do elemento procurado.
 */
function closestSibling($origin, selector) {
    var containers = ['.widget', '.update-panel', 'form'];

    for (var i = 0; i < containers.length; i++) {
        var $found = $origin.closest(containers[i]).find(selector);

        if ($found.length)
            return $found;
    }

    return $();
}

/**
 * Dá nome e estado aos botões que abrem o menu de comandos do registro.
 */
Accessibility.nameRecordCommandMenus = function () {
    $(RECORD_COMMANDS_TOGGLE).each(function () {
        prepareMenuButton($(this));
    });
};

/**
 * Torna o botão de mais opções alcançável pelo teclado. Ele é um div no servidor, então não
 * recebe foco nem é anunciado como acionável sem estes atributos.
 */
Accessibility.prepareExtraOptionsButtons = function () {
    $(EXTRA_OPTIONS_BUTTON).each(function () {
        var $button = $(this);

        makeFocusableButton($button);
        prepareMenuButton($button);
    });
};

/**
 * Declara que o elemento abre um menu e inicia o estado como fechado.
 *
 * @param {jQuery} $button botão que abre o menu.
 */
function prepareMenuButton($button) {
    $button.attr('aria-haspopup', 'true');

    if (!$button.attr('aria-expanded'))
        $button.attr('aria-expanded', 'false');

    nameFromTitle($button);
}

/**
 * Torna acionável pelo teclado um elemento que o servidor não renderizou como botão.
 *
 * @param {jQuery} $element elemento com ação de clique.
 */
function makeFocusableButton($element) {
    $element.attr('role', 'button');
    $element.attr('tabindex', '0');
}

/**
 * Torna o botão de pesquisa do campo lookup alcançável pelo teclado e o nomeia.
 *
 * Ele é uma âncora sem href e com onclick, portanto não recebia foco (WCAG 2.1.1). O nome vem do
 * rótulo do campo, que é a única informação disponível no cliente — o botão não tem título.
 */
Accessibility.nameLookupSearchButtons = function () {
    $(LOOKUP_SEARCH_BUTTON).each(function () {
        var $button = $(this);

        if ($button.attr('href') || $button.attr('data-wes-named'))
            return;

        $button.attr('data-wes-named', 'true');

        makeFocusableButton($button);
        $button.attr('aria-haspopup', 'dialog');
        $button.find('i').attr('aria-hidden', 'true');

        var labelId = $button.closest('.input-group').find(LOOKUP_SELECTION).attr('aria-labelledby');

        if (labelId)
            $button.attr('aria-labelledby', labelId);
    });
};

/**
 * Tira da árvore de acessibilidade a barra de comandos repetida no rodapé.
 *
 * Widget.js copia os comandos do topo para o rodapé quando o widget é mais alto que a janela. É
 * repetição visual: anunciar "Salvar" duas vezes esconde do usuário que é o mesmo comando, e
 * criaria duas paradas de TAB para a mesma ação. Elemento focável dentro de aria-hidden produz
 * foco fantasma, por isso as cópias também saem da ordem de TAB.
 */
Accessibility.hideDuplicatedCommandBar = function () {
    var $bar = $(FOOTER_COMMANDS_BAR);

    $bar.attr('aria-hidden', 'true');
    $bar.find('a, button, input, select, textarea').attr('tabindex', '-1');
};

/**
 * Usa o title do elemento como nome acessível, quando ele ainda não tem nome.
 *
 * @param {jQuery} $element elemento a nomear.
 */
function nameFromTitle($element) {
    if ($element.attr('aria-label') || $element.attr('aria-labelledby'))
        return;

    var title = $element.attr('title');

    if (title)
        $element.attr('aria-label', title);
}

/* ------------------------------------------------------------------------------------------
 * Foco no conteúdo principal
 * ----------------------------------------------------------------------------------------*/

/**
 * Move o foco para o landmark de conteúdo principal.
 *
 * É o destino do foco quando não há um elemento anterior para restaurar, e o último recurso
 * quando o elemento que tinha o foco não existe mais no DOM. Assim o foco nunca fica no body,
 * onde o usuário de leitor de tela não sabe onde está.
 *
 * Páginas de modal e iframe usam a master enxuta, que não tem o landmark: nesses casos a
 * função apenas devolve falso e quem chamou segue com o comportamento anterior.
 *
 * @returns {boolean} verdadeiro quando o landmark existe e recebeu o foco.
 */
Accessibility.focusMainContent = function () {
    var main = document.getElementById(Accessibility.MAIN_CONTENT_ID);

    if (!main)
        return false;

    // O landmark tem tabindex -1: recebe foco por script, sem entrar na ordem de TAB.
    main.focus();

    return true;
};

/* ------------------------------------------------------------------------------------------
 * Anúncios para o leitor de tela
 * ----------------------------------------------------------------------------------------*/

/**
 * Anuncia uma mensagem ao leitor de tela sem mudar o foco nem a tela.
 *
 * A região vive na master page, fora do conteúdo: uma região aria-live só é anunciada se já
 * existir no DOM antes de o texto mudar, e o conteúdo dos widgets é recriado a cada postback.
 *
 * @param {string} message texto já traduzido.
 */
Accessibility.announce = function (message) {
    var region = document.getElementById(Accessibility.LIVE_REGION_ID);

    if (!region || !message)
        return;

    // Reatribuir o mesmo texto não dispara anúncio; limpar antes garante a repetição.
    region.textContent = '';

    setTimeout(function () {
        region.textContent = message;
    }, 100);
};

/**
 * Registra que o postback em andamento foi disparado pelo filtro de um grid, para que o
 * resultado possa ser anunciado quando a resposta chegar.
 *
 * @param {Element} postBackElement elemento que disparou o postback.
 */
Accessibility.rememberFilterPostBack = function (postBackElement) {
    Accessibility.pendingFilterWidgetId = null;

    var $filter = $(postBackElement).closest('.filter-search-form');

    if (!$filter.length)
        return;

    var $widget = $filter.closest('.widget');

    if ($widget.length)
        Accessibility.pendingFilterWidgetId = $widget.attr('id');
};

/**
 * Anuncia quantos registros o filtro trouxe. Sem isto o usuário de leitor de tela filtra e não
 * recebe nenhum retorno, porque a tabela muda fora do seu campo de percepção.
 */
Accessibility.announceFilterResult = function () {
    var widgetId = Accessibility.pendingFilterWidgetId;

    if (!widgetId)
        return;

    Accessibility.pendingFilterWidgetId = null;

    // As linhas de dados do grid são as que possuem o atributo rel, que carrega o índice.
    var count = $('#' + widgetId).find('table ' + GRID_ROW).length;

    if (count === 0) {
        Accessibility.announce(BennerTranslations.noRecordFound);
        return;
    }

    Accessibility.announce(count + ' ' + BennerTranslations.recordsFound);
};

export default Accessibility;
